<template>
  <section class="invoice-add-wrapper">
    <validation-observer ref="simpleRules">
      <b-form>
        <b-row>
          <!-- ============================================ -->
          <!-- PANEL IZQUIERDO — Datos de la cotización -->
          <!-- ============================================ -->
          <b-col
            cols="12"
            md="4"
            xl="3"
          >
            <b-card
              class="quote-add-wrapper"
              no-body
            >
              <b-card-body>
                <!-- Cliente -->
                <h6>
                  <b>Cliente:</b>
                </h6>
                <validation-provider
                  v-slot="{ errors }"
                  name="cliente"
                  rules="required"
                >
                  <b-form-group>
                    <v-select
                      v-if="availableCustomers"
                      ref="select"
                      input-id="select-customers"
                      placeholder="Selecciona un cliente"
                      :options="availableCustomers"
                      :label="customerSelectLabel"
                      :clearable="false"
                      @input="val => setCustomerData(val)"
                      @search="searchCustomers"
                    >
                      <template #list-header>
                        <li
                          v-if="userData().role === 'admin'"
                          class="add-new-customer-header d-flex align-items-center my-50"
                          @click="customerAddSidebarActive = true"
                        >
                          <feather-icon
                            icon="PlusIcon"
                            size="16"
                          />
                          <span class="align-middle ml-25">Nuevo cliente</span>
                        </li>
                      </template>
                      <template #option="option">
                        {{ option.nit }} - {{ option.label }}
                      </template>
                      <template slot="no-options">
                        No se encontraron clientes
                      </template>
                    </v-select>
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>

                <div v-if="customerData.id">
                  <!-- Info del cliente -->
                  <h5 class="mb-50">
                    #{{ customerData.id }} - {{ customerData.name }}
                  </h5>
                  <p class="mb-1">
                    <span class="font-weight-bold">NIT: </span>
                    {{ (customerData.nit_cf === 'Y') ? `CF - ${customerData.nit}` : customerData.nit }}
                    <br>
                    <span class="font-weight-bold">Cel: </span>{{ customerData.cellphone || '-' }}
                    <br>
                    <span class="font-weight-bold">Tel: </span>{{ customerData.phone || '-' }}
                  </p>

                  <!-- Dirección -->
                  <div class="mb-1">
                    <span class="font-weight-bold">Dirección: </span>
                    <template v-if="customerData.addresses && customerData.addresses.length > 0">
                      <div
                        v-if="selectedAddress"
                        class="ml-1 mt-50"
                      >
                        <b>{{ selectedAddress.name }}: </b>
                        {{ selectedAddress.address }}, {{ selectedAddress.municipality }}, {{ selectedAddress.department }}
                        <template v-if="selectedAddress.reference">
                          <br><b>Ref: </b>{{ selectedAddress.reference }}
                        </template>
                        <div class="mt-50">
                          <a
                            href="#"
                            @click.prevent="showAddressSelector = true"
                          >+ Ver más direcciones</a>
                        </div>
                      </div>
                      <div v-else>
                        <a
                          href="#"
                          @click.prevent="showAddressSelector = true"
                        >Seleccionar dirección</a>
                      </div>
                    </template>
                    <template v-else>
                      <a
                        href="#"
                        class="ml-1"
                        @click.prevent="openAddressSidebar"
                      >Agregar dirección</a>
                    </template>
                  </div>

                  <!-- Fecha y lista de precios -->
                  <b-row>
                    <b-col
                      cols="12"
                      md="6"
                    >
                      <validation-provider
                        v-slot="{ errors }"
                        name="fecha"
                        rules="required"
                      >
                        <b-form-group>
                          <h6>
                            <b>Fecha:</b>
                          </h6>
                          <b-form-datepicker
                            v-model="customerQuoteData.quote_date"
                            :date-format-options="{ year: 'numeric', month: 'numeric', day: 'numeric' }"
                            locale="es"
                            placeholder="Fecha"
                            selected-variant="warning"
                            size="sm"
                          />
                          <small class="text-danger">{{ errors[0] }}</small>
                        </b-form-group>
                      </validation-provider>
                    </b-col>

                    <b-col
                      cols="12"
                      md="6"
                    >
                      <b-form-group>
                        <h6>
                          <b>Lista de precios:</b>
                        </h6>
                        <v-select
                          v-model="customerQuoteData.price_list_id"
                          :options="priceLists"
                          label="name"
                          :reduce="priceList => priceList.id"
                          placeholder="Lista"
                          :disabled="!isAdmin || orderLines.length > 0"
                          @input="onPriceListChange"
                        />
                        <small
                          v-if="!isAdmin"
                          class="text-muted"
                        >
                          Es la lista asignada al cliente y define el precio de venta.
                        </small>
                        <small
                          v-else-if="orderLines.length > 0"
                          class="text-warning"
                        >
                          Elimina los productos para cambiar la lista
                        </small>
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <!-- Moneda y tipo de venta -->
                  <b-row>
                    <b-col
                      cols="12"
                      md="6"
                    >
                      <b-form-group>
                        <h6>
                          <b>Moneda:</b>
                        </h6>
                        <v-select
                          v-model="customerQuoteData.currency_id"
                          :options="currencies"
                          label="name"
                          :reduce="currency => currency.id"
                          placeholder="Moneda"
                        >
                          <template #option="option">
                            {{ option.name }} {{ option.symbol }}
                          </template>
                        </v-select>
                      </b-form-group>
                    </b-col>

                    <b-col
                      cols="12"
                      md="6"
                    >
                      <b-form-group>
                        <h6>
                          <b>Tipo de venta:</b>
                        </h6>
                        <b-form-select
                          v-model="customerQuoteData.origin_sale"
                          :options="saleOriginOptions"
                          size="sm"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <!-- Vendedor (solo admin) -->
                  <div v-if="userData().role === 'admin'">
                    <h6>
                      <b>Vendedor:</b>
                    </h6>
                    <validation-provider
                      v-slot="{ errors }"
                      name="vendedor"
                      rules="required"
                    >
                      <b-form-group>
                        <v-select
                          v-if="vendorsOptions"
                          :options="vendorsOptions"
                          label="label"
                          placeholder="Selecciona un vendedor"
                          @input="val => customerQuoteData.user_id = val ? val.id : null"
                        >
                          <template #option="option">
                            {{ option.id }} - {{ option.label }}
                          </template>
                          <template slot="no-options">
                            No se encontraron vendedores
                          </template>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </b-form-group>
                    </validation-provider>
                  </div>

                  <!-- Observaciones -->
                  <b-form-group>
                    <h6>
                      <b>Observaciones:</b>
                    </h6>
                    <b-form-textarea
                      v-model="customerQuoteData.observations"
                      placeholder="Comentarios de la cotización"
                      rows="2"
                    />
                  </b-form-group>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- ============================================ -->
          <!-- PANEL DERECHO — Catálogo + Grilla -->
          <!-- ============================================ -->
          <b-col
            v-if="customerData.id"
            cols="12"
            md="8"
            xl="9"
          >
            <!-- Catálogo -->
            <b-card no-body>
              <b-card-body class="pb-50">
                <h6 class="mb-1">
                  <b>Catálogo de productos:</b>
                </h6>

                <b-form-input
                  v-model="searchQuery"
                  placeholder="Buscar por código o nombre de estilo..."
                  class="mb-1"
                />

                <div
                  v-if="!customerQuoteData.price_list_id"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-warning mb-50"
                  />
                  <p class="text-muted mb-0">
                    Selecciona una lista de precios para ver el catálogo
                  </p>
                </div>

                <div
                  v-else-if="!searchQuery.trim() && estilos.length === 0"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    Busca un producto por código o nombre para comenzar
                  </p>
                </div>

                <div
                  v-else-if="isLoadingProducts"
                  class="text-center py-2"
                >
                  <b-spinner
                    variant="primary"
                    class="mb-50"
                  />
                  <p class="text-muted mb-0">
                    Buscando productos...
                  </p>
                </div>

                <div
                  v-else-if="estilos.length === 0 && searchQuery.trim().length >= 2"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    No se encontraron productos para "{{ searchQuery }}"
                  </p>
                </div>

                <div
                  v-else-if="estilos.length > 0"
                  class="quote-add-wrapper__catalogo"
                >
                  <div
                    v-for="estilo in estilos"
                    :key="estilo.code + '-' + estilo.color"
                    class="estilo-card"
                    :class="{ 'estilo-card--added': isStyleAdded(estilo.code, estilo.color) }"
                    @click="onToggleStyle(estilo)"
                  >
                    <img
                      :src="getEstiloThumbnail(estilo)"
                      :alt="estilo.code"
                      class="estilo-card__thumb"
                      @error="onEstiloImageError"
                    >
                    <div class="estilo-card__info">
                      <span class="estilo-card__code">
                        {{ estilo.code }}
                      </span>
                      <small class="text-muted">
                        {{ estilo.color }}
                      </small>
                    </div>
                    <div class="estilo-card__action">
                      <feather-icon
                        v-if="isStyleAdded(estilo.code, estilo.color)"
                        icon="CheckCircleIcon"
                        size="20"
                        class="text-success"
                      />
                      <feather-icon
                        v-else
                        icon="PlusCircleIcon"
                        size="20"
                        class="text-primary"
                      />
                    </div>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Grilla de la cotización -->
            <b-card no-body>
              <b-card-body>
                <h6 class="mb-1">
                  <b>Detalle de la cotización ({{ activeLines.length }} productos):</b>
                </h6>

                <div class="order-grid">
                  <div
                    v-if="gridRows.length === 0"
                    class="text-center py-2"
                  >
                    <feather-icon
                      icon="ShoppingCartIcon"
                      size="32"
                      class="text-muted mb-1"
                    />
                    <p class="text-muted mb-0">
                      Selecciona un estilo del catálogo para agregar sus tallas
                    </p>
                  </div>

                  <div
                    v-else
                    class="table-responsive"
                  >
                    <table class="table table-sm order-grid__table">
                      <thead>
                        <tr>
                          <th class="order-grid__th-style">
                            Estilo-Color
                          </th>
                          <th
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__th-size"
                          >
                            {{ size }}
                          </th>
                          <th class="order-grid__th-num">
                            TOTAL
                          </th>
                          <th class="order-grid__th-price">
                            Precio unit
                          </th>
                          <th class="order-grid__th-num">
                            MONEDA
                          </th>
                          <th class="order-grid__th-amount">
                            TOTAL EN {{ currencySymbol }}
                          </th>
                          <th class="order-grid__th-actions" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in gridRows"
                          :key="row.key"
                        >
                          <td class="order-grid__style-cell">
                            <span class="font-weight-bold">
                              {{ row.estilo }}
                            </span>
                            <small class="text-muted d-block">
                              {{ row.color }}
                            </small>
                          </td>
                          <td
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__qty-cell"
                          >
                            <template v-if="row.sizeMap[size]">
                              <!-- Venta caída: la cantidad solicitada no se limita al stock; el faltante se muestra, no se bloquea -->
                              <input
                                type="number"
                                class="order-grid__qty-input"
                                :class="{
                                  'order-grid__qty-input--shortfall': shortfallOf(row.sizeMap[size]) > 0,
                                  'order-grid__qty-input--empty': row.sizeMap[size].cantidad === 0,
                                }"
                                :value="row.sizeMap[size].cantidad || ''"
                                :title="shortfallOf(row.sizeMap[size]) > 0 ? `Faltan ${shortfallOf(row.sizeMap[size])} (disponible ${row.sizeMap[size].stock})` : `Disponible ${row.sizeMap[size].stock}`"
                                placeholder="0"
                                min="0"
                                @change="onQtyChange(row.sizeMap[size], $event)"
                              >
                              <small
                                class="order-grid__stock-hint"
                                :class="{ 'order-grid__stock-hint--shortfall': shortfallOf(row.sizeMap[size]) > 0 }"
                              >
                                {{ shortfallOf(row.sizeMap[size]) > 0 ? `Faltan ${shortfallOf(row.sizeMap[size])}` : row.sizeMap[size].stock }}
                              </small>
                              <!-- Talla con oferta: precio de lista tachado y precio ofertado. -->
                              <div
                                v-if="lineOffer(row.sizeMap[size])"
                                class="order-grid__offer"
                                :title="`Oferta: ahorra ${currencySymbol}${lineOffer(row.sizeMap[size]).ahorro.toFixed(2)} por unidad`"
                              >
                                <s>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).lista.toFixed(2) }}</s>
                                <strong>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).oferta.toFixed(2) }}</strong>
                              </div>
                            </template>
                          </td>
                          <td class="text-center font-weight-bold">
                            {{ row.totalQty }}
                          </td>
                          <td>
                            <div class="price-input">
                              <span class="price-input__display price-input__display--locked">
                                {{ currencySymbol }}{{ Number(row.precioActual).toFixed(2) }}
                              </span>
                              <span
                                v-if="row.offerSizes > 0 && !row.isOffer"
                                class="price-input__badge price-input__badge--offer"
                                title="Hay tallas con precio ofertado; su precio aparece en la celda de la talla"
                              >
                                oferta en {{ row.offerSizes }} {{ row.offerSizes === 1 ? 'talla' : 'tallas' }}
                              </span>
                              <span
                                v-if="row.isOffer"
                                class="price-input__badge price-input__badge--offer"
                                title="Precio ofertado vigente; pisa el precio de la lista del cliente"
                              >
                                oferta
                              </span>
                              <span
                                v-else-if="row.isCustomPrice && !row.offerSizes"
                                class="price-input__badge"
                                title="Precio propio de la lista; no sigue al precio base del estilo"
                              >
                                lista
                              </span>
                              <span
                                v-else
                                class="price-input__original"
                                title="Esta variante sigue el precio base del estilo"
                              >
                                base
                              </span>
                            </div>
                          </td>
                          <td class="text-center text-muted">
                            {{ currencyCode }}
                          </td>
                          <td class="text-right font-weight-bold order-grid__row-total">
                            {{ currencySymbol }}{{ row.totalAmount }}
                          </td>
                          <td class="text-center">
                            <button
                              type="button"
                              class="btn btn-sm btn-flat-danger"
                              @click="onRemoveStyle(row.key)"
                            >
                              <feather-icon
                                icon="TrashIcon"
                                size="14"
                              />
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div class="order-grid__footer">
                      <span
                        v-if="totalShortfall > 0"
                        class="order-grid__shortfall-total"
                      >
                        Faltante por stock: {{ totalShortfall }} unidades
                      </span>
                      <span>Total:</span>
                      <span class="order-grid__grand-total">
                        {{ currencySymbol }}{{ grandTotal }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Minimo de unidades de la lista (PRECIO VOLUMEN); el servidor tambien bloquea. -->
                <price-list-unit-rule-notice
                  :customer-id="customerQuoteData.customer_id"
                  :price-list-id="customerQuoteData.price_list_id"
                  :units="unitRuleUnits"
                  documento="cotizacion"
                  @change="unitRuleOk = $event"
                />

                <b-button
                  v-ripple.400="'rgba(255, 159, 67, 0.15)'"
                  variant="primary"
                  block
                  class="mt-1"
                  :disabled="isSavingQuote || activeLines.length === 0 || !unitRuleOk"
                  @click="saveData"
                >
                  <template v-if="isSavingQuote">
                    <b-spinner
                      small
                      class="mr-50"
                    />
                    Guardando...
                  </template>
                  <template v-else>
                    Guardar cotización
                  </template>
                </b-button>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <customers-address-selector
          :show.sync="showAddressSelector"
          :customer-id="customerQuoteData.customer_id"
          :selected-address-id="customerQuoteData.shipping_address_id"
          @address-selected="selectAddress"
        />

        <customers-address-form
          v-if="customerData && customerData.id"
          :show.sync="showAddressSidebar"
          :customer-id="customerData.id"
          :customer-data="customerData"
          :address-data="null"
          @refresh-data="refreshCustomerAddresses"
        />

        <customers-add
          :customer-add-sidebar-active.sync="customerAddSidebarActive"
          @refreshData="getCustomers"
        />
      </b-form>
    </validation-observer>
  </section>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import { isAdmin } from '@/utils/auth'
import { lineOffer, rowPriceSummary, catalogVariant } from '../components/gridOffer'
import { coveredUnits } from '../components/unitRuleStatus'
import PriceListUnitRuleNotice from '../components/PriceListUnitRuleNotice.vue'
import CustomersAdd from '../../contacts/customers/CustomersAdd.vue'
import CustomersAddressSelector from '../../contacts/customers/CustomersAddressSelector.vue'
import CustomersAddressForm from '../../contacts/customers/CustomersAddressForm.vue'

export default {
  components: {
    PriceListUnitRuleNotice,
    vSelect,
    CustomersAdd,
    ValidationProvider,
    ValidationObserver,
    CustomersAddressSelector,
    CustomersAddressForm,
  },
  directives: {
    Ripple,
  },

  data() {
    return {
      customerQuoteData: {
        customer_id: '',
        user_id: this.userData().id,
        price_list_id: null,
        currency_id: null,
        quote_date: this.getCurrentDate(),
        observations: '',
        origin_sale: 'Cliente Final',
        products: [],
        shipping_address_id: null,
        shipping_address_text: '',
      },

      availableCustomers: [],
      customerAddSidebarActive: false,
      customerSelectLabel: 'label',
      customerData: {},

      priceLists: [],
      currencies: [],

      allVariants: [],
      estilos: [],
      searchQuery: '',
      isLoadingProducts: false,

      orderLines: [],

      vendorsOptions: [],
      saleOriginOptions: [
        { value: 'Cliente Final', text: 'Cliente Final' },
        { value: 'Venta en línea', text: 'Venta en línea' },
      ],

      showAddressSelector: false,
      showAddressSidebar: false,
      selectedAddress: null,

      isSavingQuote: false,
      // Lo informa PriceListUnitRuleNotice: false bloquea el boton de guardar.
      unitRuleOk: true,
      timeout: null,

      isAdmin: isAdmin(),
    }
  },

  computed: {
    // Unidades para el minimo de la lista: en cotizacion, las cubiertas por
    // stock; en pedido, las del pedido (ver unitRuleStatus.js).
    unitRuleUnits() {
      return coveredUnits(this.activeLines)
    },

    activeLines() {
      return this.orderLines.filter(line => line.cantidad > 0)
    },

    currencySymbol() {
      if (!this.customerQuoteData.currency_id) return 'Q'
      const selected = this.currencies.find(c => c.id === this.customerQuoteData.currency_id)
      return selected ? (selected.symbol || 'Q') : 'Q'
    },

    currencyCode() {
      if (!this.customerQuoteData.currency_id) return 'GTQ'
      const selected = this.currencies.find(c => c.id === this.customerQuoteData.currency_id)
      return selected && selected.code ? selected.code : 'GTQ'
    },

    allSizes() {
      const sizes = new Set()
      this.orderLines.forEach(line => sizes.add(line.size))
      const arr = [...sizes]
      arr.sort((a, b) => {
        const numA = parseFloat(a)
        const numB = parseFloat(b)
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
        return a.localeCompare(b)
      })
      return arr
    },

    gridRows() {
      const groups = {}

      this.orderLines.forEach(line => {
        const key = `${line.estilo}-${line.color}`
        if (!groups[key]) {
          groups[key] = {
            key,
            estilo: line.estilo,
            color: line.color,
            precioActual: line.precioActual,
            precioBase: line.precioBase,
            isCustomPrice: line.isCustomPrice,
            isOffer: line.isOffer,
            sizeMap: {},
            lines: [],
          }
        }
        groups[key].sizeMap[line.size] = line
        groups[key].lines.push(line)
      })

      return Object.values(groups).map(group => ({
        ...group,
        ...rowPriceSummary(group.lines),
        totalQty: group.lines.reduce((sum, l) => sum + l.cantidad, 0),
        totalAmount: Number(
          group.lines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
        ).toFixed(2),
      }))
    },

    grandTotal() {
      return Number(
        this.orderLines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
      ).toFixed(2)
    },

    totalShortfall() {
      return this.orderLines.reduce((sum, l) => sum + this.shortfallOf(l), 0)
    },
  },

  watch: {
    searchQuery(val) {
      if (this.timeout) clearTimeout(this.timeout)
      const query = val.trim()
      if (query.length < 2) {
        if (query.length === 0) {
          this.allVariants = []
          this.estilos = []
        }
        return
      }
      this.timeout = setTimeout(() => {
        this.searchProductsFromServer()
      }, 400)
    },
  },

  created() {
    this.getCustomers()
    this.getPriceLists()
    this.getCurrencies()
    if (this.userData().role === 'admin') {
      this.getVendors()
    }
  },

  methods: {
    lineOffer,

    userData() {
      return JSON.parse(localStorage.getItem('DuttonAuthUser'))
    },

    isStyleAdded(code, color) {
      return this.orderLines.some(l => l.estilo === code && l.color === color)
    },

    getCustomers() {
      this.availableCustomers = []
      axios
        .get('customer?perPage=10&sortField=id&sortDesc=desc&filterField=status&filterValue=Y')
        .then(response => {
          response.data.data.forEach(el => {
            this.availableCustomers.push({
              label: el.name,
              id: el.id,
              nit: el.nit,
            })
          })
        })
        .catch(error => { this.showErrors(error) })
    },

    searchCustomers(search, loading) {
      if (this.isNIT(search)) {
        this.customerSelectLabel = 'nit'
      } else {
        this.customerSelectLabel = 'label'
      }

      if (search.length) {
        loading(true)
        if (this.timeout) clearTimeout(this.timeout)
        this.timeout = setTimeout(() => {
          axios
            .get(`customer?filterField=status&filterValue=Y&query=${search}`)
            .then(response => {
              loading(false)
              this.availableCustomers = []
              response.data.data.forEach(el => {
                this.availableCustomers.push({
                  label: el.name,
                  id: el.id,
                  nit: el.nit,
                })
              })
            })
            .catch(error => { this.showErrors(error) })
        }, 300)
      } else {
        this.getCustomers()
      }
    },

    isNIT(value) {
      return /^\d+$/.test(value) || value === 'CF'
    },

    getPriceLists() {
      axios.get('price-list-active')
        .then(response => { this.priceLists = response.data.data })
        .catch(error => { this.showErrors(error) })
    },

    getCurrencies() {
      axios.get('currency-active')
        .then(response => { this.currencies = response.data.data })
        .catch(error => { this.showErrors(error) })
    },

    getVendors() {
      this.vendorsOptions = []
      const filterArray = [{ field: 'active', value: 'Y' }]
      axios
        .get(`system/users?filters=${JSON.stringify(filterArray)}`)
        .then(response => {
          response.data.data.forEach(el => {
            this.vendorsOptions.push({ label: el.name, id: el.id })
          })
        })
        .catch(error => { this.showErrors(error) })
    },

    setCustomerData(customer) {
      this.orderLines = []
      this.allVariants = []
      this.estilos = []
      this.searchQuery = ''

      if (!customer) {
        this.customerData = {}
        this.customerQuoteData.customer_id = ''
        this.customerQuoteData.price_list_id = null
        this.selectedAddress = null
        return
      }

      this.customerQuoteData.customer_id = customer.id
      this.selectedAddress = null
      this.customerQuoteData.shipping_address_id = null
      this.customerQuoteData.shipping_address_text = ''
      this.customerQuoteData.price_list_id = null

      axios.get(`customer/${customer.id}`)
        .then(response => {
          this.customerData = response.data.data

          if (this.customerData.price_list_id) {
            this.customerQuoteData.price_list_id = this.customerData.price_list_id
          } else {
            this.makeToast('warning', 'Cliente sin lista de precios', 'Este cliente no tiene una lista de precios asignada.')
          }

          if (this.customerData.addresses && this.customerData.addresses.length > 0) {
            const defaultAddr = this.customerData.addresses.find(a => a.is_default)
            this.selectAddress(defaultAddr || this.customerData.addresses[0])
          }
        })
        .catch(error => { this.showErrors(error) })
    },

    onPriceListChange() {
      if (this.orderLines.length > 0) {
        this.$bvModal
          .msgBoxConfirm('Al cambiar la lista de precios se eliminarán los productos de la cotización. ¿Continuar?', {
            title: 'Confirmar cambio',
            size: 'sm',
            okVariant: 'warning',
            cancelVariant: 'outline-dark',
            okTitle: 'SI',
            cancelTitle: 'NO',
            centered: true,
          })
          .then(confirmed => {
            if (confirmed) {
              this.orderLines = []
              this.allVariants = []
              this.estilos = []
              this.searchQuery = ''
            } else {
              this.customerQuoteData.price_list_id = this.customerData.price_list_id
            }
          })
      } else {
        this.allVariants = []
        this.estilos = []
        this.searchQuery = ''
      }
    },

    searchProductsFromServer() {
      if (!this.customerQuoteData.price_list_id || !this.customerQuoteData.customer_id) return

      const query = this.searchQuery.trim()
      if (query.length < 2) return

      this.isLoadingProducts = true

      const params = {
        price_list_id: this.customerQuoteData.price_list_id,
        customer_id: this.customerQuoteData.customer_id,
        search: query,
        perPage: 50,
      }

      axios.get('product-variant-with-prices', { params })
        .then(response => {
          this.allVariants = response.data.data.data || []
          this.groupByEstilo()
          this.isLoadingProducts = false
        })
        .catch(error => {
          this.isLoadingProducts = false
          this.showErrors(error)
        })
    },

    groupByEstilo() {
      const map = {}

      this.allVariants.forEach(v => {
        const code = v.code || v.label.split('-').slice(0, -1).join('-') || v.label
        const key = `${code}-${v.color}`

        if (!map[key]) {
          map[key] = {
            code,
            description: v.descripcion || '',
            color: v.color || '',
            image: v.imagen || null,
            variants: [],
          }
        }

        map[key].variants.push(catalogVariant(v))
      })

      Object.values(map).forEach(estilo => {
        estilo.variants.sort((a, b) => {
          const numA = parseFloat(a.size)
          const numB = parseFloat(b.size)
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
          return a.size.localeCompare(b.size)
        })
      })

      this.estilos = Object.values(map)
    },

    onToggleStyle(estilo) {
      const key = `${estilo.code}-${estilo.color}`
      if (this.isStyleAdded(estilo.code, estilo.color)) {
        this.onRemoveStyle(key)
        return
      }

      estilo.variants.forEach(v => {
        const exists = this.orderLines.some(l => l.sku === v.sku)
        if (!exists) {
          this.orderLines.push({
            sku: v.sku,
            estilo: estilo.code,
            color: estilo.color,
            descripcion: v.descripcion || estilo.description,
            precioBase: v.base_price,
            precioActual: v.precio,
            precioLista: v.precio_lista,
            cantidad: 0,
            productVariantId: v.id,
            presentacion: v.presentacion,
            size: v.size,
            imagen: estilo.image,
            isCustomPrice: v.is_custom_price,
            isOffer: v.is_offer,
            stock: v.stock,
            categoria: v.categoria,
          })
        }
      })
    },

    onRemoveStyle(styleKey) {
      this.orderLines = this.orderLines.filter(
        line => `${line.estilo}-${line.color}` !== styleKey,
      )
    },

    onQtyChange(line, event) {
      const { target } = event
      const val = parseInt(target.value, 10)
      let qty
      if (Number.isNaN(val) || val < 0) {
        target.value = 0
        qty = 0
      } else {
        // Sin tope por stock: la cotización registra la cantidad solicitada por el cliente
        qty = val
        target.value = qty || ''
      }
      const stored = this.orderLines.find(l => l.sku === line.sku)
      if (stored) stored.cantidad = qty
    },

    shortfallOf(line) {
      if (!line || line.stock === null || line.stock === undefined) return 0
      return Math.max(0, (line.cantidad || 0) - line.stock)
    },

    getEstiloThumbnail(estilo) {
      if (estilo.image && estilo.image.path) {
        return buildStorageUrl(estilo.image.path)
      }
      return NO_IMAGE_URL
    },

    onEstiloImageError(e) {
      e.target.src = NO_IMAGE_URL
    },

    openAddressSidebar() {
      this.showAddressSidebar = true
      this.showAddressSelector = false
    },

    selectAddress(address) {
      if (!address) return
      this.selectedAddress = address
      this.customerQuoteData.shipping_address_id = address.id
      this.customerQuoteData.shipping_address_text = `${address.name}: ${address.address} - ${address.municipality} / ${address.department} ${address.reference ? `/ Referencia: ${address.reference}` : ''}`
    },

    refreshCustomerAddresses() {
      this.showAddressSidebar = false
      axios
        .get(`customer/${this.customerData.id}`)
        .then(response => {
          this.customerData = response.data.data
          this.$nextTick(() => { this.showAddressSelector = true })
        })
        .catch(error => { this.showErrors(error) })
    },

    saveData() {
      if (this.activeLines.length === 0) {
        this.makeToast('warning', 'Alerta', 'No se han agregado cantidades a ningún producto.')
        return
      }

      if (!this.customerQuoteData.price_list_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar una lista de precios.')
        return
      }

      if (!this.customerQuoteData.currency_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar una moneda.')
        return
      }

      if (!this.customerQuoteData.user_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar un vendedor.')
        return
      }

      this.$bvModal
        .msgBoxConfirm('¿Deseas crear la cotización?', {
          title: 'Confirmar',
          size: 'sm',
          okVariant: 'primary',
          cancelVariant: 'outline-dark',
          okTitle: 'SI',
          cancelTitle: 'NO',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (!value) return

          this.isSavingQuote = true

          const payload = {
            ...this.customerQuoteData,
            products: this.activeLines.map(line => ({
              product_id: line.productVariantId,
              quantity: line.cantidad,
              price: line.precioActual,
            })),
          }

          axios
            .post('customer-quote', payload)
            .then(async response => {
              if (response.data.res) {
                this.makeToast('success', 'Nueva cotización', `Se ha solicitado la cotización correctamente: #${response.data.data.Id}.`)
                this.isSavingQuote = false
                await new Promise(resolve => { window.setTimeout(resolve, 500) })
                this.$emit('addQuoteTab', parseInt(response.data.data.Id, 10))
              } else {
                throw response
              }
            })
            .catch(error => {
              this.isSavingQuote = false
              this.showErrors(error)
            })
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.quote-add-wrapper {
  .add-new-customer-header {
    padding: $options-padding-y $options-padding-x;
    color: $success;

    &:hover {
      background-color: rgba($success, 0.12);
    }
  }

  &__catalogo {
    max-height: 300px;
    overflow-y: auto;
    padding-right: 4px;
  }
}

.estilo-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fafafa;
  }

  &--added {
    border-color: #28c76f;
    background: #f0faf4;
  }

  &__thumb {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid #d8dee9;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__code {
    font-weight: 700;
    font-size: 13px;
    color: #1a3a5c;
  }

  &__action {
    flex-shrink: 0;
  }
}

.price-input {
  display: flex;
  align-items: center;
  gap: 6px;

  &__display {
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;

    &--locked {
      cursor: default;
      color: #1a3a5c;
    }
  }

  &__original {
    font-size: 10px;
    color: #b9b9c3;
    text-transform: uppercase;
  }

  &__badge--offer {
    background: #ffe6e6 !important;
    color: #ea5455 !important;
  }

  &__badge {
    font-size: 10px;
    background: #fff3e8;
    color: #ff9f43;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 500;
  }

  &__field {
    width: 90px;
    padding: 2px 6px;
    border: 1px solid #1a3a5c;
    border-radius: 4px;
    font-size: 14px;
    outline: none;

    &:focus {
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.2);
    }
  }
}

.order-grid {
  &__table {
    font-size: 12px;
    white-space: nowrap;
    margin-bottom: 0;

    th {
      font-size: 10px;
      text-transform: uppercase;
      color: #6e6b7b;
      font-weight: 600;
      padding: 6px 4px;
      text-align: center;
      background: #f8f9fa;
      border-bottom: 2px solid #1a3a5c;
    }

    td {
      vertical-align: middle;
      padding: 4px;
      border-bottom: 1px solid #ebe9f1;
    }
  }

  &__th-style {
    min-width: 120px;
    text-align: left !important;
  }

  &__th-size {
    min-width: 48px;
  }

  &__th-num {
    min-width: 55px;
  }

  &__th-price {
    min-width: 110px;
  }

  &__th-amount {
    min-width: 110px;
    text-align: right !important;
  }

  &__th-actions {
    width: 40px;
  }

  &__style-cell {
    background: #fafbfc;
    border-right: 1px solid #ebe9f1;
    padding-left: 8px !important;
  }

  &__qty-cell {
    text-align: center;
  }

  &__qty-input {
    width: 40px;
    height: 28px;
    text-align: center;
    border: 1px solid #d8dee9;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 0;
    background: #fff;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus {
      outline: none;
      border-color: #1a3a5c;
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.15);
    }

    &--empty {
      background: #fafbfc;
      color: #b9b9c3;
      border-style: dashed;
    }

    &--shortfall {
      border-color: #ff9f43;
      background: #fff8f0;
      color: #b35c00;
    }
  }

  // Talla con oferta: precio de lista tachado y ofertado debajo de la cantidad.
  &__offer {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
    margin-top: 2px;
    font-size: 0.75rem;

    s {
      color: #82868b;
    }

    strong {
      color: #ea5455;
    }
  }

  &__stock-hint {
    display: block;
    font-size: 9px;
    color: #b9b9c3;
    margin-top: 1px;

    &--shortfall {
      color: #ff9f43;
      font-weight: 600;
    }
  }

  &__shortfall-total {
    margin-right: auto;
    font-size: 13px;
    font-weight: 600;
    color: #ff9f43;
  }

  &__row-total {
    white-space: nowrap;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 12px 8px;
    border-top: 2px solid #1a3a5c;
    font-size: 16px;
    font-weight: 600;
  }

  &__grand-total {
    color: #1a3a5c;
    font-size: 20px;
  }
}
</style>
